// Display helpers for the supply work order surfaces (UX-20261101-01 + 20).
//
// - Readable references: the real API returns UUIDs for the work order id and
//   the order reference; the demo data uses snake ids (wo_2026_0141). Both are
//   shown as a short, upper-case reference instead of the raw id.
// - QC gate: dispatch is allowed only when the LATEST quality-control event is
//   a pass. A rework recorded after a pass closes the gate again. The server
//   (/dispatch) applies the same rule.

import type { WorkOrderEvent } from '../../mocks/data/work-orders'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function readableRef(raw: string, prefix: string): string {
  const value = raw.trim()
  if (!value) return value
  if (UUID_RE.test(value)) return `${prefix}-${value.slice(0, 8).toUpperCase()}`
  // Demo snake ids: wo_2026_0141 → WO-2026-0141, so_2026_0539 → SO-2026-0539.
  return value.replace(/_/g, '-').toUpperCase()
}

export function formatWorkOrderRef(id: string): string {
  return readableRef(id, 'WO')
}

export function formatOrderRef(ref: string): string {
  return readableRef(ref, 'ORD')
}

export function hasPassedLatestQc(events: WorkOrderEvent[] | undefined): boolean {
  const qcEvents = (events ?? []).filter(
    (e) => e.kind === 'qc_completed' || e.kind === 'rework_completed',
  )
  if (qcEvents.length === 0) return false
  return qcEvents[qcEvents.length - 1].kind === 'qc_completed'
}

/** Pull a readable message out of an API error body (FastAPI detail object or
 * MSW problem string). */
export function apiErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message: unknown }).message
      if (typeof message === 'string') return message
    }
  }
  return `HTTP ${status}`
}

export function apiErrorCode(body: unknown): string | null {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (detail && typeof detail === 'object' && 'code' in detail) {
      const code = (detail as { code: unknown }).code
      if (typeof code === 'string') return code
    }
  }
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code: unknown }).code
    if (typeof code === 'string') return code
  }
  return null
}
